/**
 * Dispatcher component for rich blocks.
 *
 * Maps the `component` field from the block registry to a React component.
 * Falls back to a generic JSON display for unknown component names so the
 * UI never crashes on an unrecognised block type.
 *
 * When a block is already answered/expired, it renders in a muted state.
 */
import { Typography } from '@components/typography';
import { Box, useTheme } from '@mui/material';
import { AskUserQuestionBlock } from './AskUserQuestionBlock';
import { ButtonsBlock } from './ButtonsBlock';
import { ImagePreviewBlock } from './ImagePreviewBlock';
import { MultiChoiceBlock } from './MultiChoiceBlock';
import { StatusCardBlock } from './StatusCardBlock';
import { YesNoBlock } from './YesNoBlock';
import type { RichBlockComponent, RichBlockProps } from './richBlockTypes';

/** Registry of component name → React component */
const BLOCK_COMPONENTS: Record<string, RichBlockComponent> = {
  AskUserQuestionBlock,
  YesNoBlock,
  StatusCardBlock,
  ButtonsBlock,
  MultiChoiceBlock,
  ImagePreviewBlock,
};

const InteractiveBlocks = [
  'AskUserQuestionBlock',
  'YesNoBlock',
  'ButtonsBlock',
  'MultiChoiceBlock',
];

/**
 * Renders a rich block by dispatching to the appropriate component.
 */
export const AgentChatRichBlock = ({ block, onRespond }: RichBlockProps) => {
  const Component = BLOCK_COMPONENTS[block.component];
  const { typography } = useTheme();
  return (
    <Box
      sx={{
        // Expired blocks stay full opacity — they're still clickable
        // (re-sends the selection as a new message). Only answered blocks dim.
        opacity:
          block.status === 'answered' && InteractiveBlocks.includes(block.component) ? 0.7 : 1,
        transition: 'opacity 0.3s',
      }}
      data-testid={`rich-block-${block.blockType}`}
    >
      {Component ? (
        <Component block={block} onRespond={onRespond} />
      ) : (
        <Box
          component="pre"
          sx={{
            bgcolor: 'background.default',
            borderRadius: 1,
            p: 1,
            ...typography.caption2,
            overflowX: 'auto',
            color: 'text.light',
            // mt: 0.25,
            my: 0,
            maxHeight: 300,
            overflowY: 'auto',
          }}
        >
          <Typography variant="caption2" color="agentChat.message.agent.foreground.light">
            Unknown block: {block.component} ({block.blockType})
          </Typography>
          <pre style={{ fontSize: '0.7rem', overflow: 'auto', maxHeight: 200, marginBottom: 0 }}>
            {JSON.stringify(block.payload, null, 2)}
          </pre>
        </Box>
      )}
    </Box>
  );
};
